import { useState, type ReactNode } from "react";
import type { ExamDisposition } from "@/atoms/labelingAtoms";
import { useCallerEmail } from "@/hooks/useCallerEmail";
import {
  useCaseTechnicalHoldMutation,
  useClearCaseUnassessableMutation,
  useMarkCaseUnassessableMutation,
  type CaseDisposition,
  type CaseHold,
  type UnassessableInput,
  type UnassessableReason,
} from "@/hooks/useLabelingQueries";
import { FaExclamationTriangle, FaUndo } from "@/utils/icons";

interface ReasonOption {
  value: UnassessableReason;
  label: string;
}

const REASONS: ReasonOption[] = [
  { value: "scout_only", label: "Scout / localizer only" },
  { value: "wrong_study_type", label: "Wrong study type" },
  { value: "non_diagnostic", label: "Non-diagnostic imaging" },
  { value: "viewer_issue", label: "Viewer issue" },
];

// Codes from acquisition_scope.json#/reader_eligibility. No "eligibility
// uncertain" option (D30): a reader who is unsure reads the exam.
const EXCLUDE_REASONS: ReasonOption[] = [
  {
    value: "no_dedicated_arterial_cta",
    label: "No dedicated arterial CTA (e.g. CT only or perfusion only)",
  },
  { value: "unsupported_modality_or_protocol", label: "Unsupported modality or protocol" },
  { value: "below_minimum_age", label: "Patient below minimum age" },
  {
    value: "base_intracranial_coverage_or_quality_failure",
    label: "Intracranial coverage or quality fails",
  },
  { value: "other_locked_eligibility_criterion", label: "Other (note required)" },
];
const NOTE_REQUIRED: UnassessableReason = "other_locked_eligibility_criterion";

const reasonLabel = (reason: UnassessableReason) =>
  [...REASONS, ...EXCLUDE_REASONS].find((option) => option.value === reason)?.label ?? reason;

const linkButtonClass =
  "mt-2 flex items-center gap-1 text-[10px] text-fg-muted underline underline-offset-2 hover:text-fg disabled:opacity-40";
const actionButtonClass =
  "h-8 text-[11px] font-medium rounded-md border border-border-strong bg-surface-2 text-fg-muted transition-colors hover:text-fg disabled:opacity-30";

function MutationError({ error }: { error: unknown }) {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return (
    <p role="alert" className="mt-1 text-[10px] text-warn">
      {typeof detail === "string" && detail.trim()
        ? detail
        : "Could not update the case. Please try again."}
    </p>
  );
}

interface UnassessableCaseControlProps {
  projectId: string;
  studyInstanceUid: string;
  dispositions?: CaseDisposition[];
  disabled?: boolean;
  /** Worklist policy; when set, readers get Exclude and Technical problem instead. */
  examDisposition?: ExamDisposition;
  examExcluded?: boolean;
  technicalHolds?: CaseHold[];
  /** Called after a saved Exclude so the reader moves to the next case. */
  onExcluded?: () => void;
}

export function UnassessableCaseControl(props: UnassessableCaseControlProps) {
  const key = `${props.projectId}:${props.studyInstanceUid}`;
  return props.examDisposition ? (
    <ExamDispositionControl key={key} {...props} />
  ) : (
    <CaseDispositionControl key={key} {...props} />
  );
}

function CaseDispositionControl({
  projectId,
  studyInstanceUid,
  dispositions = [],
  disabled = false,
}: UnassessableCaseControlProps) {
  const [editing, setEditing] = useState(false);
  const markMutation = useMarkCaseUnassessableMutation(projectId, studyInstanceUid);
  const clearMutation = useClearCaseUnassessableMutation(projectId, studyInstanceUid);
  const email = useCallerEmail();
  const ownFlag = dispositions.find((item) => item.recorded_by.toLowerCase() === email);
  const otherFlags = dispositions.filter((item) => item !== ownFlag);
  const busy = disabled || !email || markMutation.isPending || clearMutation.isPending;
  const confirm = (input: UnassessableInput) => {
    if (busy) return;
    markMutation.mutate(input, { onSuccess: () => setEditing(false) });
  };

  return (
    <div className="shrink-0 space-y-2 border-t border-border px-3 py-2">
      {!email && <p className="text-[10px] text-fg-muted">Sign in again to manage your flag.</p>}
      {ownFlag ? (
        <OwnFlag disposition={ownFlag} busy={busy} clearMutation={clearMutation} />
      ) : editing ? (
        <DispositionForm
          disabled={busy}
          mutation={markMutation}
          onConfirm={confirm}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          disabled={busy}
          className={`w-full ${actionButtonClass}`}
        >
          <FaExclamationTriangle className="mr-1.5 inline h-3 w-3" />
          Mark unassessable
        </button>
      )}
      {otherFlags.length > 0 && (
        <div className="max-h-32 space-y-2 overflow-y-auto text-[10px] text-fg-muted">
          {otherFlags.map((disposition) => (
            <div key={disposition.recorded_by} className="rounded border border-border p-2">
              <p className="mb-1 break-all">Recorded by {disposition.recorded_by}</p>
              <DispositionDetails disposition={disposition} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface OwnFlagProps {
  disposition: CaseDisposition;
  busy: boolean;
  clearMutation: ReturnType<typeof useClearCaseUnassessableMutation>;
}

function OwnFlag({ disposition, busy, clearMutation }: OwnFlagProps) {
  return (
    <div className="rounded-md border border-border-strong bg-surface-2 p-2.5">
      <DispositionDetails disposition={disposition} />
      <button
        type="button"
        onClick={() => !busy && clearMutation.mutate()}
        disabled={busy}
        className={linkButtonClass}
      >
        <FaUndo className="h-2.5 w-2.5" />
        {clearMutation.isPending ? "Clearing…" : "Clear my flag"}
      </button>
      {clearMutation.isError && <MutationError error={clearMutation.error} />}
    </div>
  );
}

/**
 * Neurovascular reader study, Step 1 of the reader instructions: Exclude
 * (whole exam, applies to every reader's worklist under D12) or Technical
 * problem (this reader's case waits for the study team).
 */
function ExamDispositionControl({
  projectId,
  studyInstanceUid,
  dispositions = [],
  disabled = false,
  examExcluded = false,
  technicalHolds = [],
  onExcluded,
}: UnassessableCaseControlProps) {
  const [editing, setEditing] = useState(false);
  const markMutation = useMarkCaseUnassessableMutation(projectId, studyInstanceUid);
  const clearMutation = useClearCaseUnassessableMutation(projectId, studyInstanceUid);
  const holdMutation = useCaseTechnicalHoldMutation(projectId, studyInstanceUid);
  const email = useCallerEmail();
  const ownFlag = dispositions.find((item) => item.recorded_by.toLowerCase() === email);
  const ownHold = technicalHolds.some((hold) => hold.recorded_by.toLowerCase() === email);
  const busy =
    disabled ||
    !email ||
    markMutation.isPending ||
    clearMutation.isPending ||
    holdMutation.isPending;
  const exclude = (input: UnassessableInput) => {
    if (busy) return;
    markMutation.mutate(input, {
      onSuccess: () => {
        setEditing(false);
        onExcluded?.();
      },
    });
  };

  let body: ReactNode;
  if (ownFlag) {
    body = <OwnFlag disposition={ownFlag} busy={busy} clearMutation={clearMutation} />;
  } else if (examExcluded) {
    // Blinded: the peer's identity and reason are not shown to readers.
    body = (
      <p className="rounded-md border border-border-strong bg-surface-2 p-2.5 text-[11px] font-medium text-fg">
        Excluded by another reader. No answers are needed.
      </p>
    );
  } else if (editing) {
    body = (
      <ExcludeForm
        disabled={busy}
        mutation={markMutation}
        onConfirm={exclude}
        onCancel={() => setEditing(false)}
      />
    );
  } else {
    body = (
      <ExamActions
        busy={busy}
        ownHold={ownHold}
        holdMutation={holdMutation}
        onExclude={() => setEditing(true)}
      />
    );
  }

  return (
    <div className="shrink-0 space-y-2 border-t border-border px-3 py-2">
      {!email && <p className="text-[10px] text-fg-muted">Sign in again to manage your flag.</p>}
      {body}
    </div>
  );
}

interface ExamActionsProps {
  busy: boolean;
  ownHold: boolean;
  holdMutation: ReturnType<typeof useCaseTechnicalHoldMutation>;
  onExclude: () => void;
}

function ExamActions({ busy, ownHold, holdMutation, onExclude }: ExamActionsProps) {
  return (
    <>
      {ownHold && (
        <div className="rounded-md border border-dashed border-border-strong bg-surface-2 p-2.5">
          <p className="text-[11px] font-medium text-fg">Technical problem reported</p>
          <p className="mt-0.5 text-[10px] leading-4 text-fg-muted">
            This case is on hold. The study team will fix it and return it to you.
          </p>
          <button
            type="button"
            onClick={() => !busy && holdMutation.mutate(false)}
            disabled={busy}
            className={linkButtonClass}
          >
            <FaUndo className="h-2.5 w-2.5" />
            Clear technical problem
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onExclude}
          disabled={busy}
          className={`flex-1 ${actionButtonClass}`}
        >
          Exclude
        </button>
        {!ownHold && (
          <button
            type="button"
            onClick={() => !busy && holdMutation.mutate(true)}
            disabled={busy}
            className={`flex-1 ${actionButtonClass}`}
          >
            {holdMutation.isPending ? "Saving…" : "Technical problem"}
          </button>
        )}
      </div>
      {holdMutation.isError && <MutationError error={holdMutation.error} />}
    </>
  );
}

interface FormProps {
  disabled: boolean;
  mutation: ReturnType<typeof useMarkCaseUnassessableMutation>;
  onConfirm: (input: UnassessableInput) => void;
  onCancel: () => void;
}

function FormButtons({
  mutation,
  onCancel,
  onConfirm,
  confirmDisabled,
  confirmLabel,
}: Pick<FormProps, "mutation" | "onCancel"> & {
  onConfirm: () => void;
  confirmDisabled: boolean;
  confirmLabel: string;
}) {
  return (
    <>
      {mutation.isError && <MutationError error={mutation.error} />}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={mutation.isPending}
          className="flex-1 sidebar-btn h-8 text-[11px]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={confirmDisabled}
          className="flex-1 sidebar-btn-primary h-8 text-[11px] disabled:opacity-40"
        >
          {mutation.isPending ? "Saving…" : confirmLabel}
        </button>
      </div>
    </>
  );
}

function ExcludeForm({ disabled, mutation, onConfirm, onCancel }: FormProps) {
  // No default reason: the reader must choose one.
  const [reason, setReason] = useState<UnassessableReason | null>(null);
  const [note, setNote] = useState("");
  const noteMissing = reason === NOTE_REQUIRED && !note.trim();
  return (
    <div role="group" aria-label="Exclude exam" className="space-y-2">
      <fieldset className="space-y-1" disabled={disabled}>
        <legend className="mb-1 text-[10px] font-medium text-fg-muted">
          Why is this exam excluded?
        </legend>
        {EXCLUDE_REASONS.map((option) => (
          <label key={option.value} className="flex items-start gap-2 text-[11px] text-fg">
            <input
              type="radio"
              name="exclude-reason"
              value={option.value}
              checked={reason === option.value}
              onChange={() => setReason(option.value)}
              className="mt-0.5"
            />
            {option.label}
          </label>
        ))}
      </fieldset>
      {reason === NOTE_REQUIRED && (
        <textarea
          value={note}
          disabled={disabled}
          onChange={(event) => setNote(event.target.value.slice(0, 500))}
          placeholder="Which criterion is not met, and what shows it?"
          aria-label="Exclusion note"
          rows={2}
          className="w-full resize-none rounded border border-border-strong bg-surface-2 px-2 py-1.5 text-[11px] text-fg placeholder:text-fg-muted"
        />
      )}
      <FormButtons
        mutation={mutation}
        onCancel={onCancel}
        onConfirm={() =>
          reason && onConfirm({ reason, note: reason === NOTE_REQUIRED ? note.trim() : undefined })
        }
        confirmDisabled={disabled || !reason || noteMissing}
        confirmLabel="Exclude & Next"
      />
      <p className="text-[9px] leading-3 text-fg-muted">
        Excludes this exam for every reader. Your saved answers are kept.
      </p>
    </div>
  );
}

function DispositionDetails({ disposition }: { disposition: CaseDisposition }) {
  return (
    <>
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-fg">
        <FaExclamationTriangle className="h-3 w-3 shrink-0" />
        {disposition.disposition === "excluded" ? "Excluded" : "Unassessable"} ·{" "}
        {reasonLabel(disposition.reason)}
      </div>
      {disposition.note && (
        <p className="mt-1 break-words text-[10px] leading-4 text-fg-muted">{disposition.note}</p>
      )}
    </>
  );
}

function DispositionForm({ disabled, mutation, onConfirm, onCancel }: FormProps) {
  const [reason, setReason] = useState<UnassessableReason>("scout_only");
  const [note, setNote] = useState("");
  return (
    <div role="dialog" aria-label="Mark case unassessable" className="space-y-2">
      <label className="block text-[10px] font-medium text-fg-muted" htmlFor="unassessable-reason">
        Why can’t this case be assessed?
      </label>
      <select
        id="unassessable-reason"
        value={reason}
        disabled={disabled}
        onChange={(event) => setReason(event.target.value as UnassessableReason)}
        className="w-full rounded border border-border-strong bg-surface-2 px-2 py-1.5 text-[11px] text-fg"
      >
        {REASONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <textarea
        value={note}
        disabled={disabled}
        onChange={(event) => setNote(event.target.value.slice(0, 500))}
        placeholder="Optional note"
        aria-label="Unassessable note"
        rows={2}
        className="w-full resize-none rounded border border-border-strong bg-surface-2 px-2 py-1.5 text-[11px] text-fg placeholder:text-fg-muted"
      />
      <FormButtons
        mutation={mutation}
        onCancel={onCancel}
        onConfirm={() => onConfirm({ reason, note: note.trim() || undefined })}
        confirmDisabled={disabled}
        confirmLabel="Confirm"
      />
      <p className="text-[9px] leading-3 text-fg-muted">
        This records an exclusion; it does not create Absent findings.
      </p>
    </div>
  );
}
